import { useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';

// Tecton
import { useGetFcos } from '../../../api/fcos';
import { FCOType, WorkspaceFCOContainer } from '../../../core/types/fcoTypes';
import { useUserSettings } from '../../context/UserSettingsContext';
import { ServiceRoutes, getFeatureServiceRouteName, getFeatureServiceRoutes } from './featureServiceUtils';

// @tecton
import { Loading, ViewLayout } from '@lithos';
import { DataflowContainer } from '@tecton/ComponentRedesign/Dataflow';
import { dataflowSpecSelector, idFormatter } from '@tecton/ComponentRedesign/Dataflow/workspaceDataflowSpecSelector';

import FeatureServiceOverview from '@tecton/ComponentRedesign/FeatureServiceOverview';
import { useGetDatasets } from '../../../api/datasets';
import { useGetFeatureServiceSummary } from '../../../api/serviceSummary';
import { FeatureServiceSummary } from '../../../core/types/featureServiceSummary';
import FeatureServiceDetailMonitoring from './FeatureServiceDetailMonitoring';
import FeatureServiceMaterialization from './FeatureServiceMaterialization';
import FeatureServiceDatasets from './FeatureServiceDatasets';
import FeatureServiceJobs from './FeatureServiceJobs';
import FeatureViewsList from '../featureViews/FeatureViewsList';

const FeatureServiceOutlet = () => {
  /*
    This contains the header portion of the Feature Service
    - Breadcrumbs
    - Tabs
    - Outlet to the content
  */
  const navigation = useNavigate();
  const { name } = useParams();
  const { workspace } = useUserSettings();
  const location = useLocation();
  const featureRoutes = getFeatureServiceRouteName(location.pathname);
  const [selectedTab, setSelectedTab] = useState<ServiceRoutes>(featureRoutes);

  const { data, isLoading } = useGetFcos(workspace ?? '', {
    select: (data: WorkspaceFCOContainer) => {
      return {
        featureServices: data?.featureServices ?? [],
        featureViews: data?.featureViews ?? [],
        idToFcoMap: data?.idToFcoMap,
      };
    },
  });

  const routeNames = Object.keys(getFeatureServiceRoutes());

  const headerProps = {
    breadcrumbs: {
      root: { label: workspace ?? '', url: `/repo/${workspace}` },
      items: [{ label: 'Services', url: `/repo/${workspace}/services` }, { label: name }],
    },
    title: name,
    tabs: {
      items: routeNames.map((routeName) => ({ label: routeName, value: routeName })),
      selectedTab: selectedTab,
      setSelectedTab: (tab: ServiceRoutes) => {
        const routes = getFeatureServiceRoutes();
        navigation(routes[tab]);
        setSelectedTab(tab);
      },
    },
  };

  if (isLoading) {
    return <ViewLayout header={headerProps} content={<Loading />} />;
  }

  return <ViewLayout header={headerProps} content={<Outlet />} />;
};

const FeatureServiceContainer = () => {
  const { workspace } = useUserSettings();
  const { name } = useParams();
  const { data, isLoading } = useGetFcos(workspace ?? '', {});
  const { data: datasetsData, isLoading: datasetsLoading } = useGetDatasets(workspace ?? '');
  const { data: summaryStateData, isLoading: summaryIsLoading } = useGetFeatureServiceSummary(workspace!, name ?? '');

  if (isLoading || datasetsLoading || summaryIsLoading) {
    return <></>;
  }

  const featureService = data?.featureServices.find((fs: any) => {
    return fs.name === name;
  });

  if (!featureService) {
    // We couldn't find the feature service so we will redirect you to the Feature Service lists.
    // This can happen when you switch workspaces and that particular FS detail doesn't exist in the workspace you are switching to.
    return <Navigate to={`../${workspace}/feature-services`} />;
  }

  const featureViews = featureService.allFeatureViews.map((fvId: string) => data.idToFcoMap[fvId]);

  const asData = dataflowSpecSelector(data, name ?? '', {
    id: idFormatter(featureService),
    type: FCOType.FEATURE_SERVICE,
  });

  const featureServiceRoutes = getFeatureServiceRoutes();

  return (
    <Routes>
      <Route element={<FeatureServiceOutlet />}>
        <Route index element={<Navigate to={featureServiceRoutes['Dataflow']} replace />} />
        <Route path={featureServiceRoutes['Dataflow']} element={<DataflowContainer data={asData} />} />
        <Route
          path={featureServiceRoutes['Metadata']}
          element={
            <FeatureServiceOverview
              service={featureService}
              fcos={data}
              workspace={workspace ?? ''}
              servingSummary={{ ...summaryStateData } as FeatureServiceSummary}
              // servingSummary={mockFetchServiceSummary()}
              curlSummary={summaryStateData}
            />
          }
        />
        <Route
          path={featureServiceRoutes['Materialization']}
          element={<FeatureServiceMaterialization featureService={featureService} featureViews={data.featureViews} />}
        />
        <Route
          path={featureServiceRoutes['Feature Views']}
          element={<FeatureViewsList items={featureViews} idToFcoMap={data.idToFcoMap} workspace={workspace ?? ''} />}
        />
        <Route
          path={featureServiceRoutes['Monitoring']}
          element={
            <FeatureServiceDetailMonitoring featureService={featureService} featureViews={data?.featureViews ?? []} />
          }
        />
        <Route
          path={featureServiceRoutes['Datasets']}
          element={<FeatureServiceDatasets featureService={featureService} />}
        />
        <Route path={featureServiceRoutes['Jobs']} element={<FeatureServiceJobs featureService={featureService} />} />
      </Route>
    </Routes>
  );
};

export default FeatureServiceContainer;
